import { sendsStandardPrompt } from '@parlay-picker/shared/analysisConfig';
import { DEPRECATED_FACTORS } from '@parlay-picker/shared/deprecatedFactors';
import { canUseFactorById, getFactor } from '@parlay-picker/shared/factorRegistry';
import type { SubscriptionTier } from '@parlay-picker/shared/subscriptionTier';
import type { AnalysisConfig } from '@views/picks/constants';
import type { ReactElement } from 'react';
import { Section, SectionGroup } from './Section';
import { Toggle } from './Toggle';

interface DataSourcesSectionsProps {
  cfg: AnalysisConfig;
  expandedSections: Record<string, boolean>;
  toggleSection: (key: string) => void;
  getEnabledCount: (key: string) => number;
  getTotalCount: (key: string) => number;
  toggleAllInSection: (key: string, enable: boolean) => void;
  updateConfig: (section: string, key: string, value: boolean | number | string) => void;
  factorLimitProps: (
    checked: boolean,
    existingDisabled?: boolean,
    existingReason?: string,
  ) => { disabled: boolean; disabledReason: string | undefined };
  leagueHasPlayerProps: boolean;
  statsApiDown: boolean;
  msfDown: boolean;
  isLastStatWindow: (key: 'l5Average' | 'l10Average' | 'l20Average' | 'seasonAverage') => boolean;
  tier: SubscriptionTier;
}

const SHARP_CONTEXT_ID = 'otherFactors.includeSharpContext';
const TEAM_SIGNALS_ID = 'otherFactors.includeTeamPropSignals';

export function DataSourcesSections({
  cfg,
  expandedSections,
  toggleSection,
  getEnabledCount,
  getTotalCount,
  toggleAllInSection,
  updateConfig,
  factorLimitProps,
  leagueHasPlayerProps,
  statsApiDown,
  msfDown,
  isLastStatWindow,
  tier,
}: DataSourcesSectionsProps): ReactElement {
  const statsUnavailableReason = 'Stats service temporarily unavailable';
  const lastWindowReason = 'At least 1 stat window must stay enabled';
  const startersOn = !msfDown && cfg.otherFactors.includeProjectedStarters === true;
  const showSharpContext = canUseFactorById(tier, SHARP_CONTEXT_ID);
  const showTeamSignals =
    canUseFactorById(tier, TEAM_SIGNALS_ID) && sendsStandardPrompt(cfg.ml?.generationMode);
  // Unset means off: the server only sends sharp context when the flag is true.
  const isSharpContextOn = cfg.otherFactors.includeSharpContext ?? false;

  return (
    <SectionGroup title="Data Sources" subtitle="Controls what information the AI can see">
      {/* Player-specific sections — hidden for leagues without player props */}
      {leagueHasPlayerProps && (
        <>
          <Section
            title="Player Stats"
            sectionKey="playerStats"
            isExpanded={expandedSections.playerStats}
            onToggleExpand={toggleSection}
            enabledCount={getEnabledCount('playerStats')}
            totalCount={getTotalCount('playerStats')}
            showToggleAll
            onToggleAll={toggleAllInSection}
          >
            <Toggle
              label="L5 Average"
              description="Last 5 games average"
              checked={cfg.playerStats.l5Average}
              onChange={(v) => updateConfig('playerStats', 'l5Average', v)}
              {...factorLimitProps(
                cfg.playerStats.l5Average,
                statsApiDown || isLastStatWindow('l5Average'),
                statsApiDown
                  ? statsUnavailableReason
                  : isLastStatWindow('l5Average')
                    ? lastWindowReason
                    : undefined,
              )}
            />
            <Toggle
              label="L10 Average"
              description="Last 10 games average"
              checked={cfg.playerStats.l10Average}
              onChange={(v) => updateConfig('playerStats', 'l10Average', v)}
              {...factorLimitProps(
                cfg.playerStats.l10Average,
                statsApiDown || isLastStatWindow('l10Average'),
                statsApiDown
                  ? statsUnavailableReason
                  : isLastStatWindow('l10Average')
                    ? lastWindowReason
                    : undefined,
              )}
            />
            <Toggle
              label="L20 Average"
              description="Last 20 games average"
              checked={cfg.playerStats.l20Average}
              onChange={(v) => updateConfig('playerStats', 'l20Average', v)}
              {...factorLimitProps(
                cfg.playerStats.l20Average,
                statsApiDown || isLastStatWindow('l20Average'),
                statsApiDown
                  ? statsUnavailableReason
                  : isLastStatWindow('l20Average')
                    ? lastWindowReason
                    : undefined,
              )}
            />
            <Toggle
              label="Season Average"
              description="Full season baseline"
              checked={cfg.playerStats.seasonAverage}
              onChange={(v) => updateConfig('playerStats', 'seasonAverage', v)}
              {...factorLimitProps(
                cfg.playerStats.seasonAverage,
                statsApiDown || isLastStatWindow('seasonAverage'),
                statsApiDown
                  ? statsUnavailableReason
                  : isLastStatWindow('seasonAverage')
                    ? lastWindowReason
                    : undefined,
              )}
            />
            <p className="text-xs text-theme-text-tertiary mt-2 px-1">
              Hot/Cold trend labels auto-apply when 2+ stat windows are enabled
            </p>
            {/* Line Value Detection — uses player stat baselines, not betting history */}
            <div className="border-t border-theme-border/50 mt-2 pt-1" />
            <Toggle
              label="Line Value Detection"
              description="Alert when lines diverge from player's stat baseline"
              checked={cfg.efficiencyMetrics.lineValueDetection}
              onChange={(v) => updateConfig('efficiencyMetrics', 'lineValueDetection', v)}
              {...factorLimitProps(cfg.efficiencyMetrics.lineValueDetection)}
            />
          </Section>

          <Section
            title="Advanced Stats"
            sectionKey="advancedStats"
            isExpanded={expandedSections.advancedStats}
            onToggleExpand={toggleSection}
            enabledCount={getEnabledCount('advancedStats')}
            totalCount={getTotalCount('advancedStats')}
            showToggleAll
            onToggleAll={toggleAllInSection}
          >
            {tier === 'admin' && (
              <Toggle
                label="Usage Rate %"
                description="How often player has the ball"
                checked={false}
                onChange={() => {}}
                disabled
                disabledReason={DEPRECATED_FACTORS['advancedStats.usageRate'].reason}
              />
            )}
            {tier === 'admin' && (
              <Toggle
                label="True Shooting %"
                description="Scoring efficiency metric"
                checked={false}
                onChange={() => {}}
                disabled
                disabledReason={DEPRECATED_FACTORS['advancedStats.trueShootingPct'].reason}
              />
            )}
            <Toggle
              label="Assist Ratio"
              description="Playmaking tendency (AST/TO ratio)"
              checked={cfg.advancedStats.assistRatio}
              onChange={(v) => updateConfig('advancedStats', 'assistRatio', v)}
              {...factorLimitProps(cfg.advancedStats.assistRatio)}
            />
            <Toggle
              label="Offensive Rating"
              description="Team points per 100 possessions"
              checked={cfg.advancedStats.offensiveRating}
              onChange={(v) => updateConfig('advancedStats', 'offensiveRating', v)}
              {...factorLimitProps(cfg.advancedStats.offensiveRating)}
            />
            <Toggle
              label="Defensive Rating"
              description="Team points allowed per 100 possessions"
              checked={cfg.advancedStats.defensiveRating}
              onChange={(v) => updateConfig('advancedStats', 'defensiveRating', v)}
              {...factorLimitProps(cfg.advancedStats.defensiveRating)}
            />
          </Section>

          <Section
            title="Matchup Data"
            sectionKey="matchupData"
            isExpanded={expandedSections.matchupData}
            onToggleExpand={toggleSection}
            enabledCount={getEnabledCount('matchupData')}
            totalCount={getTotalCount('matchupData')}
            showToggleAll
            onToggleAll={toggleAllInSection}
          >
            <Toggle
              label="Player vs Team History"
              description="Avg stats vs this opponent (last 2 seasons)"
              checked={cfg.matchupData.playerVsTeamHistory}
              onChange={(v) => updateConfig('matchupData', 'playerVsTeamHistory', v)}
              {...factorLimitProps(
                cfg.matchupData.playerVsTeamHistory,
                statsApiDown,
                statsApiDown ? statsUnavailableReason : undefined,
              )}
            />
            <Toggle
              label="Player Position"
              description="PG/SG/SF/PF/C designation"
              checked={cfg.matchupData.playerPosition}
              onChange={(v) => updateConfig('matchupData', 'playerPosition', v)}
              {...factorLimitProps(
                cfg.matchupData.playerPosition,
                statsApiDown,
                statsApiDown ? statsUnavailableReason : undefined,
              )}
            />
          </Section>

          <Section
            title="Opponent Defense"
            sectionKey="opponentDefense"
            isExpanded={expandedSections.opponentDefense}
            onToggleExpand={toggleSection}
            enabledCount={getEnabledCount('opponentDefense')}
            totalCount={getTotalCount('opponentDefense')}
            showToggleAll
            onToggleAll={toggleAllInSection}
          >
            <Toggle
              label="Shot Location Defense"
              description="Opponent's FG% allowed on threes, in the paint and overall"
              checked={cfg.opponentDefense.defVsPosition}
              onChange={(v) => updateConfig('opponentDefense', 'defVsPosition', v)}
              {...factorLimitProps(cfg.opponentDefense.defVsPosition)}
            />
            <Toggle
              label="Rebounding Allowed"
              description="Opponent's rebounding weakness"
              checked={cfg.opponentDefense.reboundingAllowed}
              onChange={(v) => updateConfig('opponentDefense', 'reboundingAllowed', v)}
              {...factorLimitProps(cfg.opponentDefense.reboundingAllowed)}
            />
            <Toggle
              label="3PT Defense"
              description="Opponent 3PT% allowed"
              checked={cfg.opponentDefense.threePointDefense}
              onChange={(v) => updateConfig('opponentDefense', 'threePointDefense', v)}
              {...factorLimitProps(cfg.opponentDefense.threePointDefense)}
            />
            <Toggle
              label="Points in Paint"
              description="Opponent interior defense rating"
              checked={cfg.opponentDefense.pointsInPaint}
              onChange={(v) => updateConfig('opponentDefense', 'pointsInPaint', v)}
              {...factorLimitProps(cfg.opponentDefense.pointsInPaint)}
            />
            <Toggle
              label="Fast Break Allowed"
              description="Opponent fast break points allowed"
              checked={cfg.opponentDefense.fastBreakAllowed}
              onChange={(v) => updateConfig('opponentDefense', 'fastBreakAllowed', v)}
              {...factorLimitProps(cfg.opponentDefense.fastBreakAllowed)}
            />
          </Section>
        </>
      )}

      {/* Injuries and lineups */}
      <Section
        title="Injuries & Lineups"
        sectionKey="otherFactors"
        isExpanded={expandedSections.otherFactors}
        onToggleExpand={toggleSection}
        enabledCount={[cfg.otherFactors.injuryStatus === true, startersOn].filter(Boolean).length}
        totalCount={leagueHasPlayerProps ? 2 : 1}
      >
        <Toggle
          label="Injury Status"
          description="Day-to-day / Questionable flags"
          checked={cfg.otherFactors.injuryStatus}
          onChange={(v) => updateConfig('otherFactors', 'injuryStatus', v)}
          {...factorLimitProps(cfg.otherFactors.injuryStatus)}
        />
        {leagueHasPlayerProps && (
          <Toggle
            label="Projected Starters"
            description="Projected starting lineups for both teams"
            checked={startersOn}
            onChange={(v) => updateConfig('otherFactors', 'includeProjectedStarters', v)}
            {...factorLimitProps(
              startersOn,
              msfDown,
              msfDown ? 'Data source unavailable' : undefined,
            )}
          />
        )}
      </Section>

      {/* Market Intelligence: each toggle by its registry row's plan; team signals only in modes that send the prompt */}
      {(showSharpContext || showTeamSignals) && (
        <Section
          title="Market Intelligence"
          sectionKey="marketIntel"
          isExpanded={expandedSections.marketIntel ?? false}
          onToggleExpand={toggleSection}
          enabledCount={
            [
              showSharpContext && isSharpContextOn,
              showTeamSignals && cfg.otherFactors.includeTeamPropSignals,
            ].filter(Boolean).length
          }
          totalCount={[showSharpContext, showTeamSignals].filter(Boolean).length}
        >
          {showSharpContext && (
            <Toggle
              label={getFactor(SHARP_CONTEXT_ID)?.label ?? ''}
              description={getFactor(SHARP_CONTEXT_ID)?.description}
              checked={isSharpContextOn}
              onChange={(v) => updateConfig('otherFactors', 'includeSharpContext', v)}
            />
          )}
          {showTeamSignals && (
            <Toggle
              label={getFactor(TEAM_SIGNALS_ID)?.label ?? ''}
              description={getFactor(TEAM_SIGNALS_ID)?.description}
              checked={cfg.otherFactors.includeTeamPropSignals ?? false}
              onChange={(v) => updateConfig('otherFactors', 'includeTeamPropSignals', v)}
            />
          )}
        </Section>
      )}
    </SectionGroup>
  );
}
